import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import CognitiveLoadWithStats from "@/pages/session-detail/components/cognitive-load-with-stats/cognitive-load-with-stats";
import type { UserInfo } from "@/pages/session-detail/components/crew-cognitive-load/crew-cognitive-load.types";
import TimelineChart from "@/pages/session-detail/components/timeline-chart/timeline-chart";
import { useSessionInfo } from "@/pages/session-detail/hooks/useSessionInfo";

import {
    ArrowIconWrapper,
    PopUp,
    PopUpListItem,
    Toggle,
} from "@/components/filters/filters.styles";
import { ChevronDownSquareCornersIcon24, DirectionArrowTopRightIcon24 } from "@/components/icons";
import PageHeader from "@/components/ui/page-header/page-header";

import { DropdownFilterVariant } from "@/types/filters";

import useQueryParams from "@/utils/use-query-params";

import {
    ComponentsScrollableWrapper,
    ModalContentWrapperForScrollableContent,
    RightContentWrapperDiv,
} from "../../modal-contents.styles";
import {
    MODAL_HEIGHT_PERCENTAGE,
    MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_CREW_MODAL,
} from "../../utils/modal-content-variables";

interface CrewModalContentProps {
    subDimensionsData: any;
}

//Rendered by CrewCognitiveLoad
const CrewModalContent: React.FC<CrewModalContentProps> = ({ subDimensionsData }) => {
    const {
        isCrewFilterOpen,
        setIsCrewFilterOpen,
        crewFilterOptions,
        selectedCrewFilterOption,
        setSelectedCrewFilterOption,
        selectedCrewIndex,
        setSelectedCrewIndex,
        cognitiveLoadWithStatsData,
    } = useSessionDetailsContext();

    //map each subDimension to selected Dimension
    const mapCrewIdToSelectedCrew = useCallback(
        (selectedCrewFilterOption: string | null | undefined) => {
            return subDimensionsData?.find((crew: any) => {
                return crew?.id === selectedCrewFilterOption;
            });
        },
        [selectedCrewFilterOption, subDimensionsData],
    );

    //variable needed to fetch info about the Commander, Driver & Gunner users
    const statsArray = useMemo(() => {
        if (subDimensionsData) {
            return subDimensionsData[selectedCrewIndex]?.dataset["cognitive-load-with-stats"]?.map(
                (data: any) => {
                    return data?.datasets?.find((dataset: any) => {
                        return dataset?.code === "stats";
                    });
                },
            );
        } else {
            return cognitiveLoadWithStatsData?.map((data: any) => {
                return data?.datasets?.find((dataset: any) => dataset?.code === "stats");
            });
        }
    }, [cognitiveLoadWithStatsData, subDimensionsData, selectedCrewIndex]);

    //returns the dimensionId of a user, by its role
    const getUserInfoByRole = (statsArray: any[], role: string) => {
        return statsArray.find((item: any) => {
            return item.role.toLowerCase() === role.toLocaleLowerCase();
        });
    };

    const { getModalSubtitleDetails, logoUrl, usersInfo } = useSessionInfo({
        selectedCrewOption: selectedCrewFilterOption?.code,
    });

    const [scrollableContainerHeight, setScrollableContainerHeight] = useState(
        () =>
            window.innerHeight * MODAL_HEIGHT_PERCENTAGE -
            MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_CREW_MODAL,
    );

    const params: any = useQueryParams();
    const from: string = params.get("from");
    const userId: string = params.get("userId");
    const to: string = params.get("to");
    const scenarioName = params.get("name");
    const simulationId: string = params.get("simulationInstanceId");
    const scenarioInstanceId: string = params.get("scenarioInstanceId");

    const groupToCrewName: Record<string, string> = {};
    let crewCount = 1;

    const crews: Record<string, UserInfo[]> =
        usersInfo?.reduce((acc: Record<string, UserInfo[]>, user: UserInfo) => {
            const grouping = user.grouping;

            if (!groupToCrewName[grouping]) {
                groupToCrewName[grouping] = `crew${crewCount++}`;
            }

            const crewName = groupToCrewName[grouping];

            acc[crewName] = [...(acc[crewName] || []), user];

            return acc;
        }, {}) || {};

    //toggle filters button
    const handleFilterButtonToggle = useCallback(
        (event: any) => {
            event.stopPropagation();

            setIsCrewFilterOpen(!isCrewFilterOpen);
        },
        [isCrewFilterOpen],
    );

    const selectedCrew = useCallback(
        Object.entries(crews || [])?.reduce((acc: any, crew: any) => {
            const crewName = crew[0];
            const crewMembers = crew[1];

            if (crewName === selectedCrewFilterOption?.code) {
                return [...acc, ...crewMembers];
            }

            return acc;
        }, []),
        [crews],
    );

    //handles filter option click
    const handleFilterOptionClick = (event: any, index: number, option: any) => {
        event.stopPropagation();
        setSelectedCrewIndex(index);
        setSelectedCrewFilterOption(option);
        setIsCrewFilterOpen(false); //to close filters when option is selected
    };

    //to update the ComponentsScrollableWrapper height, when screen height changes
    useEffect(() => {
        const handleScrollableContainerResize = () => {
            const updatedHeight =
                window.innerHeight * MODAL_HEIGHT_PERCENTAGE -
                MODAL_NON_SCROLLABLE_CONTENT_HEIGHT_CREW_MODAL;
            setScrollableContainerHeight(updatedHeight);
        };

        window.addEventListener("resize", handleScrollableContainerResize);

        return () => window.removeEventListener("resize", handleScrollableContainerResize);
    }, []);

    //to update the DOM element's height, since styled components are not receiving the updated height
    useEffect(() => {
        const componentsScrollableWrapper = document.getElementById(
            "components-scrollable-wrapper",
        );

        if (componentsScrollableWrapper) {
            componentsScrollableWrapper.style.height = `${scrollableContainerHeight}px`;
        }
    }, [scrollableContainerHeight]);

    const handleCognitiveLoadWithStatsClick = useCallback(
        (event: any) => {
            const role = event.target.innerText
                .split(" ")
                .filter((word: string) => word !== "View" && word !== "Metrics")
                .join(" ");

            const user = getUserInfoByRole(statsArray, role) ?? {};

            const {
                name,
                email,
                role: userRole,
                dimensionId: userDimensionId,
            } = user as {
                name?: string;
                email?: string;
                role?: string;
                dimensionId?: string | number;
            };

            const localStorageUser = {
                name,
                email,
                role: userRole,
                id: userId,
                dimensionId: userDimensionId,
            };

            const urlRedirect = `/sessions/session-analysis?traineeName=${name}&simulationName=${role}&userId=${userId}&from=${from}&to=${to}&scenarioId=${simulationId}&scenarioInstanceId=${userDimensionId}&name=${scenarioName}&logoUrl=${logoUrl}`;

            //set the localStorage user
            localStorage.setItem("users", JSON.stringify([localStorageUser]));

            //open session details page in a new tab
            window.open(urlRedirect, "_blank");
        },
        [
            selectedCrew,
            from,
            to,
            simulationId,
            scenarioInstanceId,
            logoUrl,
            statsArray,
            scenarioName,
        ],
    );

    return (
        <ModalContentWrapperForScrollableContent>
            <PageHeader
                title={
                    selectedCrewFilterOption?.name && selectedCrewFilterOption?.scenarioName
                        ? `${selectedCrewFilterOption.name} • ${selectedCrewFilterOption.scenarioName}`
                        : "Crew"
                }
                logoUrl={logoUrl}
                subtitleDetailText={getModalSubtitleDetails}
                rightContent={
                    <RightContentWrapperDiv>
                        <Toggle
                            isOpen={isCrewFilterOpen}
                            onClick={handleFilterButtonToggle}
                            variant={DropdownFilterVariant.PrimaryGrey800}
                        >
                            <DirectionArrowTopRightIcon24 />

                            {selectedCrewFilterOption?.name}

                            <ArrowIconWrapper isOpen={isCrewFilterOpen}>
                                <ChevronDownSquareCornersIcon24 />
                            </ArrowIconWrapper>
                        </Toggle>
                        {isCrewFilterOpen && (
                            <PopUp
                                id="modal-filters-popup-id"
                                variant={DropdownFilterVariant.PrimaryGrey800}
                                popUpWidth="16.125rem"
                            >
                                {crewFilterOptions?.map((option: any, index: number) => {
                                    return (
                                        <PopUpListItem
                                            variant={DropdownFilterVariant.PrimaryGrey800}
                                            isSelected={selectedCrewIndex === index}
                                            key={option.name}
                                            onClick={(event: React.MouseEvent) => {
                                                handleFilterOptionClick(event, index, option);
                                            }}
                                        >
                                            {option.name}
                                        </PopUpListItem>
                                    );
                                })}
                            </PopUp>
                        )}
                    </RightContentWrapperDiv>
                }
            />

            <ComponentsScrollableWrapper
                //id is needed to calculate the scrollable container's height
                id="components-scrollable-wrapper"
                scrollableContainerHeight={scrollableContainerHeight}
            >
                <TimelineChart
                    isInsideModal
                    subDimensionsData={
                        mapCrewIdToSelectedCrew(selectedCrewFilterOption?.name)?.dataset?.[
                            "combined-custom-charts"
                        ]
                    }
                />

                <CognitiveLoadWithStats
                    subDimensionsData={
                        mapCrewIdToSelectedCrew(selectedCrewFilterOption?.name)?.dataset?.[
                            "cognitive-load-with-stats"
                        ]
                    }
                    onClick={(event: React.MouseEvent) => { handleCognitiveLoadWithStatsClick(event)}}
                />
            </ComponentsScrollableWrapper>
        </ModalContentWrapperForScrollableContent>
    );
};

export default CrewModalContent;
